import { useState } from "react";
import { CaretDown, CaretRight, Microphone, SealCheck, ShieldWarning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Panel, Pill } from "@/components/admin/AdminUI";
import Countdown from "@/components/admin/Countdown";
import { MediaTile } from "@/components/admin/ReviewMedia";
import { ACTIONS } from "@/components/admin/ReportActionDialog";
import ReportMessageMedia from "@/components/admin/ReportMessageMedia";
import { adminMediaUrl, mediaUrl } from "@/lib/api";
import { formatDate, formatDuration, humanize } from "@/lib/format";

const VERIFY_TONE = { verified: "success", pending: "warning", rejected: "error" };

function Badges({ g }) {
  const r = g.reported;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {g.has_underage && <Pill tone="error" className="cw-tone-error" data-testid={`report-underage-${r.id}`}><ShieldWarning size={12} weight="fill" />Underage</Pill>}
      {g.auto_hidden && <Pill tone="warning" data-testid={`report-autohidden-${r.id}`}>Auto-hidden · Needs review</Pill>}
      {g.needs_review && g.review_deadline && <Countdown to={g.review_deadline} testId={`report-countdown-${r.id}`} />}
      <Pill tone={VERIFY_TONE[r.verification_status] || "neutral"} className="capitalize" data-testid={`report-verification-${r.id}`}>
        {r.verification_status === "verified" && <SealCheck size={12} weight="fill" />}{r.verification_status || "not verified"}</Pill>
      {r.status === "banned" && <Pill tone="error">Banned</Pill>}
      {r.hidden && !r.auto_hidden && <Pill tone="warning">Hidden</Pill>}
      {r.self_hidden && <Pill>Self-hidden</Pill>}
    </div>
  );
}

function Message({ m, reportedId }) {
  const mine = m.sender_id === reportedId;
  return (
    <li className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "cw-tone-error self-start" : "self-end bg-[var(--cw-surface-tertiary)]"}`} data-testid={`report-message-${m.id}`}>
      <p className="cw-muted mb-0.5 text-[11px]">{mine ? "Reported person" : "Reporter"} · {formatDate(m.created_at)}</p>
      {m.kind === "voice" && <p className="flex items-center gap-1"><Microphone size={14} />Voice message</p>}
      {m.text && <p className="whitespace-pre-line">{m.text}</p>}
      {(m.has_media || (m.kind && m.kind !== "text")) && <ReportMessageMedia m={m} />}
    </li>
  );
}

function Snapshot({ s, reportId }) {
  if (!s) return <p className="cw-muted text-sm">No snapshot.</p>;
  const fields = [["Pronouns", s.pronouns], ["Occupation", s.occupation], ["City", s.city], ...Object.entries(s.about || {}).map(([k, v]) => [humanize(k), v])].filter(([, v]) => v);
  return (
    <div className="space-y-3" data-testid={`report-snapshot-${reportId}`}>
      <p className="cw-muted text-xs">Snapshot from {formatDate(s.taken_at)} · {s.first_name}, {s.age}</p>
      {!!s.photos?.length && <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">{s.photos.map((p, i) => <MediaTile key={p.id} src={adminMediaUrl(p.url)} alt={`Snapshot photo ${i + 1}`} testId={`report-snapshot-photo-${reportId}-${i}`} />)}</div>}
      {s.bio && <p className="cw-soft text-sm">“{s.bio}”</p>}
      {!!s.prompts?.length && <ul className="grid gap-2 md:grid-cols-3">{s.prompts.map((p, i) => <li key={i} className="rounded-xl bg-[var(--cw-surface-tertiary)] p-3 text-sm"><p className="cw-muted text-xs">{p.text}</p>{p.answer}</li>)}</ul>}
      {!!fields.length && <p className="cw-soft text-xs">{fields.map(([k, v]) => `${k}: ${humanize(v)}`).join(" · ")}</p>}
    </div>
  );
}

// Signed link (~1h), plays without auth headers.
function GlassIntro({ r }) {
  const g = r.glass_intro;
  const [failed, setFailed] = useState(false);
  if (!g) return <p className="cw-muted rounded-xl bg-[var(--cw-surface-tertiary)] px-3 py-2 text-sm" data-testid={`report-glass-missing-${r.id}`}>Intro no longer available</p>;
  const src = mediaUrl(g.url);
  return (
    <div className="space-y-2 rounded-2xl border border-border p-3" data-testid={`report-glass-${r.id}`}>
      <p className="text-sm font-medium" data-testid={`report-glass-label-${r.id}`}>Reported: glass intro ({g.kind}{g.duration_s != null ? `, ${formatDuration(g.duration_s)}` : ""})</p>
      {g.kind === "video"
        ? <video controls playsInline preload="metadata" src={src} onError={() => setFailed(true)} className="max-h-80 rounded-xl bg-black" data-testid={`report-glass-video-${r.id}`} />
        : <audio controls preload="metadata" src={src} onError={() => setFailed(true)} className="w-72 max-w-full" data-testid={`report-glass-audio-${r.id}`} />}
      {failed && <p className="cw-error text-xs" role="alert" data-testid={`report-glass-error-${r.id}`}>This browser can’t play it, or the link expired. <a href={src} target="_blank" rel="noreferrer" className="underline">Open it</a>, or reload the page for a fresh link.</p>}
    </div>
  );
}

function Report({ r, reportedId }) {
  return (
    <li className="space-y-3 py-5" data-testid={`report-item-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={r.reason === "underage" ? "error" : "brand"}>{r.reason_label}</Pill>
        <span className="cw-soft text-sm">by {r.reporter?.first_name || "someone"}{r.reporter?.age ? `, ${r.reporter.age}` : ""} · from {humanize(r.context)} · {formatDate(r.created_at)}</span>
        {r.status === "resolved" && <Pill tone="neutral" className="capitalize">{humanize(r.resolution)} by {r.resolved_by} · {formatDate(r.resolved_at)}</Pill>}
      </div>
      {r.note && <p className="text-sm">“{r.note}”</p>}
      {(r.context === "glass_intro" || r.glass_intro) && <GlassIntro r={r} />}
      {!!r.messages?.length && <ul className="flex flex-col gap-2 rounded-2xl border border-border p-3" data-testid={`report-messages-${r.id}`}>{r.messages.map((m) => <Message key={m.id} m={m} reportedId={reportedId} />)}</ul>}
      <Snapshot s={r.snapshot} reportId={r.id} />
    </li>
  );
}

export default function ReportGroup({ g, canAct, open, onAction }) {
  const [expanded, setExpanded] = useState(false);
  const r = g.reported;
  const h = g.history || {};
  return (
    <Panel className={`overflow-hidden ${g.has_underage ? "ring-2 ring-destructive/30" : ""}`} data-testid={`report-group-${r.id}`}>
      <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center">
        <button onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} className="flex min-w-0 flex-1 items-center gap-4 rounded-xl text-left" data-testid={`report-expand-${r.id}`}>
          {expanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
          <img src={mediaUrl(r.photo) || undefined} alt="" className="h-14 w-14 shrink-0 rounded-2xl bg-[var(--cw-surface-tertiary)] object-cover" />
          <div className="min-w-0">
            <p className="font-medium">{r.first_name || "No name"}{r.age ? `, ${r.age}` : ""}</p>
            <p className="cw-muted text-xs">{g.reports.length} report{g.reports.length === 1 ? "" : "s"} · {g.distinct_reporters} reporter{g.distinct_reporters === 1 ? "" : "s"} · oldest {formatDate(g.oldest_at)}</p>
            <div className="mt-2"><Badges g={g} /></div>
          </div>
        </button>
        {canAct && open && (
          <div className="flex flex-wrap gap-2" data-testid={`report-actions-${r.id}`}>
            {Object.entries(ACTIONS).map(([k, a]) => (
              <Button key={k} size="sm" variant={k === "ban" ? "destructive" : "outline"} onClick={() => onAction(g, k)} data-testid={`report-action-${k}-${r.id}`}>{a.label}</Button>
            ))}
          </div>
        )}
      </div>
      {expanded && (
        <div className="border-t border-border px-5 pb-2" data-testid={`report-details-${r.id}`}>
          <p className="cw-soft pt-4 text-sm" data-testid={`report-history-${r.id}`}>
            History: {h.total ?? 0} report{h.total === 1 ? "" : "s"} in total · {Object.entries(h.resolved || {}).map(([k, v]) => `${humanize(k)} ${v}`).join(" · ")}
          </p>
          <ul className="divide-y divide-border">{g.reports.map((x) => <Report key={x.id} r={x} reportedId={r.id} />)}</ul>
        </div>
      )}
    </Panel>
  );
}
